import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Download, ShieldAlert, FileArchive } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const DbExportPanel = () => {
    const [loadingFull, setLoadingFull] = useState(false);
    const [loadingScrubbed, setLoadingScrubbed] = useState(false);

    const downloadExport = async (scrubbed) => {
        const setLoading = scrubbed ? setLoadingScrubbed : setLoadingFull;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/db-export`, {
                params: { scrubbed },
                responseType: 'blob',
            });
            // Pull filename out of Content-Disposition; fall back if missing
            const cd = res.headers['content-disposition'] || '';
            const match = cd.match(/filename="?([^"]+)"?/);
            const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = match ? match[1] : `db_${scrubbed ? 'scrubbed' : 'full'}_export_${ts}.tar.gz`;

            const blob = new Blob([res.data], { type: 'application/gzip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            const sizeKb = (blob.size / 1024).toFixed(1);
            toast.success(`Downloaded ${filename} (${sizeKb} KB)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Export failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-dark p-6 border border-amber-500/20" data-testid="db-export-panel">
            <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-400" />
                Database Export
            </h3>
            <p className="text-xs text-[#A0AAB2] mb-4">
                Download a complete <code className="text-amber-300">mongodump</code>-format archive of every collection.
                Restore with: <code className="text-amber-300">mongorestore --db=&lt;name&gt; db_export/&lt;name&gt;</code>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-md border border-red-500/30 bg-red-500/5 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert className="w-4 h-4 text-red-400" />
                        <span className="text-sm font-semibold text-red-300">Full Export</span>
                    </div>
                    <p className="text-xs text-[#A0AAB2] mb-3">
                        Raw data including emails, phones, password hashes, bank info, IPs, transactions.
                        Share via encrypted channel only.
                    </p>
                    <Button
                        onClick={() => downloadExport(false)}
                        disabled={loadingFull}
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        data-testid="db-export-full-btn"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {loadingFull ? 'Preparing…' : 'Download Full DB'}
                    </Button>
                </div>

                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <FileArchive className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-semibold text-emerald-300">Scrubbed Export</span>
                    </div>
                    <p className="text-xs text-[#A0AAB2] mb-3">
                        Same schema and document counts but PII redacted (names, emails, phones, bank, IP, password hashes).
                        Safer for third-party developers.
                    </p>
                    <Button
                        onClick={() => downloadExport(true)}
                        disabled={loadingScrubbed}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="db-export-scrubbed-btn"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {loadingScrubbed ? 'Preparing…' : 'Download Scrubbed DB'}
                    </Button>
                </div>
            </div>

            <p className="text-[11px] text-[#7C8489] mt-3">
                Every export is logged to the audit trail with your admin email and timestamp.
            </p>
        </div>
    );
};

export default DbExportPanel;
